/* eslint-disable react/prop-types */
import {
  CONSULT_ACCEPTED,
  CONSULT_CANCELLED,
  CONSULT_FINISHED,
  CONSULT_TIMEOUT,
  CONSULT_TYPE_CHAT,
  CONSULT_TYPE_FOLLOW_UP,
  CONSULT_TYPE_VIDEO,
  CONTEXT_MENU_ALL_MEDIA,
  CONTEXT_MENU_CONSULT_FEE,
  CONTEXT_MENU_CONSULT_HISTORY,
  CONTEXT_MENU_CONSULT_PATIENT,
} from 'Constants/consults';
import { CONSULT_DETAILS_FINISH_CONSULT_MODEL_RESPONSE } from 'Constants/webchat';
import { isDurationTooShort, showModalConsultTooShort } from 'Containers/Teleconsult/Main/helpers';
import { generateIncludeParams } from 'Helpers/utils';
import { bool, func, shape } from 'prop-types';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addModal, removeModal } from 'Redux/modules/modal/actions';
import {
  closeAllConsultVideoRoom,
  closeVideo,
  consultDetails,
  consultFinish,
  getOngoingCall,
  resetConsultStatus,
} from 'Redux/modules/webChat/actions';
import { Button, Segment } from 'semantic-ui-react';

import { OnGoingCallContent } from '../../FinishConsult/ModalConsultChat';
import ContextMenuHeader from '../ContextMenuHeader';

import FeeEstimation from './FeeEstimation';
import FollowUpButton from './FollowUpButton';
import ListMenu from './ListMenu';
import ListMenuBubbleChat from './ListMenuBubbleChat';
import PatientInfo from './PatientInfo';

const consultDetailsResponseParams = {
  include: generateIncludeParams(CONSULT_DETAILS_FINISH_CONSULT_MODEL_RESPONSE),
};

const ConsultMenu = props => {
  const {
    consult,
    openPOS,
    conversation,
    getConsultFee,
    openMemoRandum,
    closeContextMenu,
    openFinishConsult,
    changeContextMenu,
    openPrescriptionModal,
    openReferralMemo,
    openMedicalCertificate,
    pendingConsultActive,
    doctor: { id: doctorId },
    consultFee,
    isMinimize,
  } = props;
  const {
    id: consultId,
    consultType,
    status,
  } = consult || {
    consultId: null,
    consultType: null,
    status: null,
  };
  const dispatch = useDispatch();
  const video = useSelector(state => state.webChat.video);
  const { id: currentDrId } = useSelector(state => state.webChat.doctor.data);
  const videoId = video?.zoomInfo?.id;
  const isFollowUp = consultType === CONSULT_TYPE_FOLLOW_UP;

  const finishConsult = async () => {
    try {
      await dispatch(consultFinish(doctorId, consultId));
    } catch (error) {
      if (error?.data?.errorCode === 'EXIST_ONGOING_VIDEO_CALL') {
        dispatch(
          addModal({
            type: 'warningModal',
            id: 1,
            data: {
              content: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Warning</div>
                  <div style={{ padding: '24px', textAlign: 'center' }}>{error?.data?.message}</div>
                </div>
              ),
              size: 'tiny',
              onOk: () => {
                dispatch(resetConsultStatus());
              },
            },
          }),
        );
      }
    }
  };

  const openFinishConsultPopup = async () => {
    if (isFollowUp) {
      openFinishConsult();
      return;
    }

    const consultDetail = await dispatch(consultDetails(consultId, consultDetailsResponseParams));
    const consultDetailData = consultDetail?.data;
    const videoCallDuration = consultDetailData?.videoCallDuration;
    const patientMessageCount = consultDetailData?.patientMessageCount;
    // Warning if the consult is too short
    if (patientMessageCount === 0 && videoCallDuration === 0) {
      await finishConsult();
    } else if (isDurationTooShort(videoCallDuration)) {
      dispatch(showModalConsultTooShort({ handleOnOk: openFinishConsult }));
    } else {
      await openFinishConsult();
    }
  };

  const onFinishConsult = async ongoingCall => {
    const ongoingCallArray = ongoingCall?.data ?? [];
    if (ongoingCall) {
      await dispatch(closeAllConsultVideoRoom(ongoingCallArray?.[0]?.consultId));
      if (ongoingCallArray?.map(e => e.id)?.includes(videoId)) {
        dispatch(closeVideo());
      }
      await openFinishConsultPopup();
    } else {
      await openFinishConsultPopup();
    }
  };

  const handleClickFinishConsult = async () => {
    const ongoingCall = await dispatch(getOngoingCall(consultId));
    const { data: ongoingCallList } = ongoingCall;
    if (ongoingCallList?.length > 0) {
      dispatch(
        addModal({
          type: 'commonModal',
          id: 6,
          data: {
            title: 'On-going Call in Teleconsult',
            content: <OnGoingCallContent />,
            size: 'tiny',
            okButton: (
              <Button
                negative
                onClick={async () => {
                  dispatch(removeModal(6));
                  await onFinishConsult(ongoingCall);
                }}>
                Force End Call
              </Button>
            ),
          },
        }),
      );
    } else {
      await onFinishConsult();
    }
  };

  useEffect(() => {
    if (consultType === CONSULT_TYPE_CHAT || consultType === CONSULT_TYPE_VIDEO) {
      getConsultFee(consultId);
    }
  }, [getConsultFee, consultId, consultType]);

  const goToAllMedia = () => {
    changeContextMenu(CONTEXT_MENU_ALL_MEDIA, { consultId }, true);
  };

  const goToFeeEstimation = () => {
    if (!isFollowUp) {
      changeContextMenu(CONTEXT_MENU_CONSULT_FEE, { consultId, hasShowFinish: false }, true);
    }
  };

  const goToPatient = () => {
    changeContextMenu(CONTEXT_MENU_CONSULT_PATIENT, { consultId }, true);
  };

  const goToConsultHistory = () => {
    changeContextMenu(CONTEXT_MENU_CONSULT_HISTORY, { consultId }, true);
  };

  const isShowFollowUp =
    status === CONSULT_FINISHED || status === CONSULT_CANCELLED || status === CONSULT_TIMEOUT;
  const isGoingConsult = status === CONSULT_ACCEPTED;

  const isMainDoctor = currentDrId === consult?.doctor?.id;
  const hasShowFinish = isGoingConsult && isMainDoctor;

  return (
    <Segment.Group className="context-menu">
      {isMinimize ? (
        <ListMenuBubbleChat
          openPOS={openPOS}
          isFollowUp={isFollowUp}
          goToPatient={goToPatient}
          goToAllMedia={goToAllMedia}
          isGoingConsult={isGoingConsult}
          openMemoRandum={openMemoRandum}
          goToConsultHistory={goToConsultHistory}
          openPrescriptionModal={openPrescriptionModal}
          openReferralMemo={openReferralMemo}
          openMedicalCertificate={openMedicalCertificate}
        />
      ) : (
        <>
          <ContextMenuHeader
            title="Consult Menu"
            closeContextMenu={closeContextMenu}
            customClass="parent-menu"
          />
          <Segment className="body menu-options">
            {conversation?.latestConsult?.forPatient && (
              <PatientInfo
                parentPatient={conversation?.latestConsult?.patient}
                patient={conversation?.latestConsult?.forPatient}
                goToPatient={goToPatient}
              />
            )}
            {consult && (
              <FeeEstimation
                consultFee={consultFee}
                consult={conversation?.latestConsult || consult}
                isFollowUp={isFollowUp}
                goToFeeEstimation={goToFeeEstimation}
              />
            )}
            <ListMenu
              openPOS={openPOS}
              isFollowUp={isFollowUp}
              goToPatient={goToPatient}
              goToAllMedia={goToAllMedia}
              isGoingConsult={isGoingConsult}
              openMemoRandum={openMemoRandum}
              goToConsultHistory={goToConsultHistory}
              openPrescriptionModal={openPrescriptionModal}
              openReferralMemo={openReferralMemo}
              openMedicalCertificate={openMedicalCertificate}
              pendingConsultActive={pendingConsultActive}
            />
            {hasShowFinish && pendingConsultActive === -1 && (
              <Button
                className="btn-finish"
                primary
                onClick={handleClickFinishConsult}
                fluid
                content="FINISH CONSULT"
              />
            )}
            {isShowFollowUp && <FollowUpButton conversation={conversation} doctorId={doctorId} />}
          </Segment>
        </>
      )}
    </Segment.Group>
  );
};

ConsultMenu.propTypes = {
  openPOS: func.isRequired,
  doctor: shape({}).isRequired,
  consult: shape({}).isRequired,
  getConsultFee: func.isRequired,
  openMemoRandum: func.isRequired,
  consultFee: shape({}).isRequired,
  closeContextMenu: func.isRequired,
  openFinishConsult: func.isRequired,
  changeContextMenu: func.isRequired,
  conversation: shape({}).isRequired,
  openPrescriptionModal: func.isRequired,
  openReferralMemo: func.isRequired,
  openMedicalCertificate: func.isRequired,
  isMinimize: bool,
};
ConsultMenu.defaultProps = {
  isMinimize: false,
};

export default memo(ConsultMenu);
